import type { Ref } from "vue";
import { computed, watch } from "vue";
import type Guid from "@/app/types/common/guid";
import {
  makeDeepRef,
  withLoading,
  wrapPromiseWithErrorHandling,
} from "@/components/composables/componentTools";
import type { AssemblyQcTest } from "@/app/services/generated/assemblyqctestService";
import {
  AssemblyTestMetricLineUpdate,
  assemblyQcTestService,
} from "@/app/services/generated/assemblyqctestService";
import type { QcTest } from "@/app/services/generated/qctestService";
import { qcTestService } from "@/app/services/generated/qctestService";
import { recipeQcTestService } from "@/app/services/generated/recipeqctestService";
import type {
  ProductionQcTest,
  ProductionQcTestMetric,
  ProductionQcTestMetricDefinition,
  ProductionQcTestMetricExpectation,
} from "@/components/pages/production/qctests/composables/qcTests";
import { isNotNullOrUndefined, toGuidMap } from "@/app/apiutils";
import type { EnumAssemblyType } from "@/app/services/generated/enums/enumAssemblyType.generated";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import QcTestCaptureAssemblyMeasurementsDialog from "@/components/pages/production/qctests/subcomponents/QcTestCaptureAssemblyMeasurementsDialog.vue";
import { EnumMetricType } from "@/app/services/generated/enums/enumMetricType.generated";
import type { Maybe } from "@/components/utils/typeHelpers";

export interface AssemblyQcTestGridItem {
  name: string;
}

export function useAssemblyQcTests(
  assemblyId: Ref<Guid | undefined>,
  assemblyType: Ref<EnumAssemblyType>
) {
  const isLoaded = makeDeepRef(false);
  const assemblyQcTests = makeDeepRef<AssemblyQcTest[]>([]);
  const allQcTests = makeDeepRef<QcTest[]>([]);

  const addableQcTests = computed<QcTest[]>(() => {
    return allQcTests.value.filter((test) => {
      if (test.header.isHidden) {
        return false;
      }

      if (
        assemblyType.value.isMaterialProduction() &&
        !test.header.isForMaterialProduction
      ) {
        return false;
      }

      if (
        assemblyType.value.isProductPackaging() &&
        !test.header.isForProductPackaging
      ) {
        return false;
      }

      return !assemblyQcTests.value.some((existingTest) =>
        existingTest.header.qcTestId.equals(test.header.id)
      );
    });
  });

  const productionQcTests = computed<ProductionQcTest[]>(() => {
    if (assemblyQcTests.value.length === 0 || allQcTests.value.length === 0) {
      return [];
    }

    const qcTestMap = toGuidMap(allQcTests.value, (test) => test.header.id);

    const qcTestMetricLineMap = toGuidMap(
      allQcTests.value.flatMap((qcTest) => qcTest.metricLines),
      (metricLine) => metricLine.id
    );

    return assemblyQcTests.value.map<ProductionQcTest>(
      (assemblyTest, sequence) => {
        const qcTest = qcTestMap.get(assemblyTest.header.qcTestId)!;

        const metrics = assemblyTest.metricLines.map<ProductionQcTestMetric>(
          (metric) => {
            const qcTestMetricLine = qcTestMetricLineMap.get(
              metric.readOnly.qcTestMetric.id
            )!;

            const expectations: ProductionQcTestMetricExpectation = {
              qcTestId: qcTest.header.id,
              qcTestMetricLineId: metric.readOnly.qcTestMetric.id,
              assemblyMetricLineId: metric.id,
              assemblyQcTestId: assemblyTest.header?.id ?? null,
              expectedStartValue: metric.expectedStartValue,
              expectedEndValue: metric.expectedEndValue,
              minimumExpectedValue: metric.minimumExpectedValue,
              maximumExpectedValue: metric.maximumExpectedValue,
              isProcessRestricted: metric.isProcessRestricted,
              expectedBooleanValue: metric.expectedBooleanValue,
            };

            const definition: ProductionQcTestMetricDefinition = {
              qcTestId: qcTest.header.id,
              qcTestMetricLineId: metric.id,
              name: metric.name,
              notes: metric.notes,
              isGraphable: metric.isGraphable,
              maximumDecimalPlaces: metric.maximumDecimalPlaces,
              maximumPermittedValue: metric.maximumPermittedValue,
              minimumPermittedValue: metric.minimumPermittedValue,
              unitSuffix: metric.unitSuffix,
              type: qcTestMetricLine?.type ?? EnumMetricType.custom,
            };

            return {
              qcTestId: qcTest.header.id,
              qcTestMetricLineId: metric.id,
              definition,
              expectations,
            };
          }
        );

        return {
          qcTestId: assemblyTest.header.qcTestId,
          assemblyQcTestId: assemblyTest.header.id,
          name: qcTest.header.name,
          sequence,
          metrics,
          canMoveUp: sequence > 0,
          canMoveDown: sequence < assemblyQcTests.value.length - 1,
          notes: qcTest.header.notes,
        };
      }
    );
  });

  const refreshAssemblyQcTests = withLoading(async () => {
    isLoaded.value = false;
    assemblyQcTests.value = assemblyId.value
      ? await assemblyQcTestService.getAssemblyQcTestsForAssembly(
          assemblyId.value
        )
      : [];

    allQcTests.value = await qcTestService.getAllQcTests();
    isLoaded.value = true;
  });

  // Update or create an expectation against a metric.
  const updateExpectation = async (
    updatedExpectations: ProductionQcTestMetricExpectation
  ) => {
    if (
      !assemblyId.value ||
      !updatedExpectations.assemblyQcTestId ||
      !updatedExpectations.assemblyMetricLineId
    ) {
      return;
    }

    const update = new AssemblyTestMetricLineUpdate();
    update.id = updatedExpectations.assemblyMetricLineId;
    update.expectedStartValue = updatedExpectations.expectedStartValue;
    update.expectedEndValue = updatedExpectations.expectedEndValue;
    update.minimumExpectedValue = updatedExpectations.minimumExpectedValue;
    update.maximumExpectedValue = updatedExpectations.maximumExpectedValue;

    await assemblyQcTestService.updateAssemblyQcTestMetricLines(
      updatedExpectations.assemblyQcTestId,
      [update]
    );
    await refreshAssemblyQcTests();
  };

  const moveTestUp = async (qcTestIdToMove: Guid) => {
    if (!assemblyId.value) {
      return;
    }

    const foundLineIndex = assemblyQcTests.value.findIndex((assemblyTest) =>
      assemblyTest.header.qcTestId.equals(qcTestIdToMove)
    );

    if (foundLineIndex > 0) {
      const stored = assemblyQcTests.value[foundLineIndex - 1];
      assemblyQcTests.value[foundLineIndex - 1] =
        assemblyQcTests.value[foundLineIndex];
      assemblyQcTests.value[foundLineIndex] = stored;
    }

    await assemblyQcTestService.setAssemblyQcTestSequences(
      assemblyId.value,
      assemblyQcTests.value.map((assemblyTest) => assemblyTest.header.id)
    );
    await refreshAssemblyQcTests();
  };

  const moveTestDown = async (qcTestIdToMove: Guid) => {
    if (!assemblyId.value) {
      return;
    }

    const foundLineIndex = assemblyQcTests.value.findIndex((assemblyTest) =>
      assemblyTest.header.qcTestId.equals(qcTestIdToMove)
    );

    if (
      foundLineIndex < assemblyQcTests.value.length - 1 &&
      foundLineIndex >= 0
    ) {
      const stored = assemblyQcTests.value[foundLineIndex + 1];
      assemblyQcTests.value[foundLineIndex + 1] =
        assemblyQcTests.value[foundLineIndex];
      assemblyQcTests.value[foundLineIndex] = stored;
    }

    await assemblyQcTestService.setAssemblyQcTestSequences(
      assemblyId.value,
      assemblyQcTests.value.map((assemblyTest) => assemblyTest.header.id)
    );
    await refreshAssemblyQcTests();
  };

  const addTests = async (qcTestIds: Guid[]) => {
    if (assemblyId.value) {
      const qcTestsToAddPromises = qcTestIds.map((id) =>
        assemblyQcTestService.addQcTestToAssembly(assemblyId.value!, id)
      );

      await Promise.all(qcTestsToAddPromises);
      await refreshAssemblyQcTests();
    }
  };

  const deleteTest = async (testToDelete: ProductionQcTest) => {
    if (testToDelete.assemblyQcTestId) {
      await assemblyQcTestService.hardDeleteAssemblyQcTest(
        testToDelete.assemblyQcTestId
      );
      await refreshAssemblyQcTests();
    }
  };

  watch(
    [assemblyId, assemblyType],
    wrapPromiseWithErrorHandling(refreshAssemblyQcTests),
    {
      immediate: true,
    }
  );

  const showRecordResultsDialog = async (
    assemblyId: Guid,
    assemblyQcTestIdToRecord?: Maybe<Guid>
  ) => {
    const results = await showModalDialog<
      void,
      typeof QcTestCaptureAssemblyMeasurementsDialog
    >(QcTestCaptureAssemblyMeasurementsDialog, {
      props: {
        assemblyId,
        assemblyQcTestIdToRecord,
      },
    });
    if (results.wasSuccessful) {
      await refreshAssemblyQcTests();
    }
  };

  return {
    isLoaded,
    addableQcTests,
    productionQcTests,
    updateExpectation,
    moveTestUp,
    moveTestDown,
    addTests,
    deleteTest,
    refreshAssemblyQcTests,
    showRecordResultsDialog,
  };
}

/**
 * Called after creating an assembly to add the qc tests from recipe on to the assembly.
 */
export async function copyRecipeQcTestsToAssembly(
  recipeId: Guid,
  assemblyId: Guid
) {
  const recipeTests =
    await recipeQcTestService.getRecipeQcTestsForRecipe(recipeId);

  for (const recipeTest of recipeTests) {
    const assemblyQcTest = await assemblyQcTestService.addQcTestToAssembly(
      assemblyId,
      recipeTest.header.qcTestId
    );

    const updates = recipeTest.metricExpectations
      .map((recipeExpectation) => {
        const matchingLine = assemblyQcTest.metricLines.find((assemblyLine) =>
          assemblyLine.readOnly.qcTestMetric.id.equals(
            recipeExpectation.qcTestMetricLineId
          )
        );
        if (matchingLine) {
          const update = new AssemblyTestMetricLineUpdate();
          update.id = matchingLine.id;
          update.expectedStartValue = recipeExpectation.expectedStartValue;
          update.expectedEndValue = recipeExpectation.expectedEndValue;
          update.minimumExpectedValue = recipeExpectation.minimumExpectedValue;
          update.maximumExpectedValue = recipeExpectation.maximumExpectedValue;
          return update;
        }

        return undefined;
      })
      .filter(isNotNullOrUndefined);

    await assemblyQcTestService.updateAssemblyQcTestMetricLines(
      assemblyQcTest.header.id,
      updates
    );
  }
}
